"use client";

import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";

export interface UnitOption<TItem extends string> {
  label: string;
  value: TItem;
  abbrev?: string;
}

interface UnitToggleProps<TItem extends string> {
  value: TItem;
  onValueChange: (value: TItem) => void;
  options: UnitOption<TItem>[];
  size?: "sm" | "default";
  className?: string;
  "aria-label"?: string;
}

export function UnitToggle<TItem extends string>({
  value,
  onValueChange,
  options,
  size = "sm",
  className,
  "aria-label": ariaLabel,
}: UnitToggleProps<TItem>) {
  return (
    <ToggleGroup
      type="single"
      value={value}
      onValueChange={(v) => {
        const selected = options.find((option) => option.value === v);
        if (selected) onValueChange(selected.value);
      }}
      size={size}
      variant="outline"
      aria-label={ariaLabel}
      className={cn(
        "inline-flex rounded-md border border-input bg-muted/30 p-0.5",
        className,
      )}
    >
      {options.map((option) => (
        <ToggleGroupItem
          key={option.value}
          value={option.value}
          aria-label={option.label}
          className={cn(
            "rounded-sm border-0 px-2 text-xs font-medium",
            "data-[state=off]:bg-transparent data-[state=off]:hover:bg-muted",
            "data-[state=on]:bg-foreground data-[state=on]:font-semibold data-[state=on]:text-background",
            size === "sm" ? "h-6" : "h-8",
            "pointer-coarse:h-11 pointer-coarse:min-w-11",
          )}
        >
          {option.abbrev ?? option.label}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}
